import { useRef, useState } from 'react';
import { Link, useNotify, useRecordContext, type RaRecord } from 'react-admin';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { SectionCard } from '../../shared/Panel';
import { clientDay } from '../../shared/dateTime';
import axiosNest from '../../api/axiosNest';
import { toErrorMessage } from './orderActionsApi';
import { recordScoped, useStillMounted } from '../../shared/recordScoped';
import { plural } from '../../shared/plural';
import { formatPricePerKm, type PricePerKm } from '../carriers/pricePerKm';
import { useFeature } from '../featureFlags/FeatureGate';
import { FEATURE_CARRIER_RELATION_PRECISION } from './listFeatures';

const NOWRAP = { whiteSpace: 'nowrap' } as const;

/**
 * Uwaga w kolumnie „Uwagi” zawija się zamiast rozpychać tabelę (e2e 08.10.2026):
 * czip bez zawijania trzymał kolumnę na ~300 px, a po dołożeniu „Dopasowania”
 * (07.10) tabela przestała się mieścić w karcie przy 1366 i 1440 px — przycisk
 * „Przygotuj zapytanie” wychodził poza kartę. Ten sam wzór co w harmonogramie
 * auta (`VehicleSchedule.tsx`). W obu wersjach karty (decyzja użytkownika
 * 23.10.2026: czip uwagi zawija się także bez przełącznika).
 */
const WRAPPING_CHIP_SX = {
  height: 'auto',
  maxWidth: 170,
  mr: 0.5,
  my: 0.25,
  '& .MuiChip-label': { whiteSpace: 'normal', py: 0.25, lineHeight: 1.25 },
} as const;

/**
 * Kolumna „$” — średnia cena za km z ostatnich frachtów (omówienie
 * 01.10.2026). Fracht przewoźnika przychodzi z TMS (`carrier_price`, od
 * 06.10.2026 w API testowym, od 16.10.2026 na produkcji), liczy API
 * (`carriers/relation-price.ts`). Brak ceny to „—” z powodem w podpowiedzi —
 * ceny nie zgadujemy.
 */
export const PRICE_PER_KM_HINT =
  'Średnia cena za km z ostatnich 10 frachtów przewoźnika na tej relacji: fracht z TMS w złotówkach (waluty obce kursem średnim NBP z dnia załadunku), w nawiasie liczba frachtów. Liczymy ją z co najmniej 3 frachtów.';

/** Komórka „$”: cena z opisem rachunku albo kreska z powodem braku. */
function PricePerKmCell({ price }: { price: PricePerKm | undefined }) {
  // Starsza odpowiedź API nie ma ceny wcale — sama kreska.
  if (!price) return <>—</>;
  if (price.pln === null) {
    return (
      <Tooltip title={price.reason ?? ''} describeChild>
        <span>—</span>
      </Tooltip>
    );
  }
  return (
    <Tooltip title={price.description ?? ''} describeChild>
      <span>{formatPricePerKm(price)}</span>
    </Tooltip>
  );
}

/** Odpowiedź `GET /carriers/suggestions/for-order/:id` (`carriers/carrier-suggestions.ts`). */
/**
 * Dokładność dopasowania relacji (07.10.2026, prezentacja 02.10 pkt 10) —
 * lustro `MATCH_PRECISIONS` z `carriers/carrier-suggestions.ts`. Trader
 * wybiera, jak podobna ma być relacja kursu; szersza = więcej przewoźników.
 * Kolejność dalej układa API (cena za km), dokładność niczego nie waży.
 */
export type MatchPrecision = 'exact' | 'postcode2' | 'postcode1' | 'country';

/** Domyślna — lista jak przed 07.10 (cała relacja kraj → kraj); bez parametru w zapytaniu. */
export const DEFAULT_PRECISION: MatchPrecision = 'country';

export const PRECISION_OPTIONS: ReadonlyArray<{ value: MatchPrecision; label: string }> = [
  { value: 'exact', label: 'Dokładnie (kod pocztowy albo miejscowość)' },
  { value: 'postcode2', label: 'Region (kraj + 2 cyfry kodu)' },
  { value: 'postcode1', label: 'Szerzej (kraj + 1 cyfra kodu)' },
  { value: 'country', label: 'Kraj → kraj (wszystkie kursy)' },
];

/** Krótka nazwa poziomu w kolumnie „Dopasowanie”; pełna z API w podpowiedzi. */
export const MATCH_LEVEL_SHORT: Readonly<Record<MatchPrecision, string>> = {
  exact: 'dokładnie',
  postcode2: 'region',
  postcode1: '1 cyfra kodu',
  country: 'kraj → kraj',
};

export function suggestionsUrl(orderId: RaRecord['id'], precision: MatchPrecision): string {
  const base = `/carriers/suggestions/for-order/${orderId}`;
  return precision === DEFAULT_PRECISION ? base : `${base}?precision=${precision}`;
}

export interface CarrierSuggestionResult {
  refusal: string | null;
  relation: { from: string; to: string } | null;
  /** Dokładność dopasowania; starsza odpowiedź może jej nie mieć. */
  precision?: MatchPrecision;
  precisionLabel?: string;
  historyDays: number;
  historyOrders: number;
  /** Jedno zdanie: po czym ułożono listę; starsza odpowiedź może go nie mieć. */
  ordering?: string | null;
  suggestions: Array<{
    carrierId: number;
    name: string;
    active: boolean | null;
    regionOrders: number;
    countryOrders: number;
    /** Poziom jego najbliższego kursu; starsza odpowiedź może go nie mieć. */
    matchLevel?: MatchPrecision;
    matchLabel?: string;
    matchedOrders?: number;
    lastOrderAt: string;
    /** Najczęstsze typy pojazdów z jego kursów na relacji; starsza odpowiedź może ich nie mieć. */
    vehicleTypes?: Array<{ type: string; orders: number }>;
    /** Kolumna „$”; starsza odpowiedź może jej nie mieć. */
    pricePerKm?: PricePerKm;
    warnings: string[];
  }>;
}

/**
 * Przewoźnicy z historią na relacji zlecenia (I.08) — dla Trade, zanim
 * zlecenie wyjdzie na giełdę.
 *
 * Kolejność układa API (06.10.2026, żądanie PM z 01.10): przewoźnicy ze
 * średnią ceną za km pierwsi, od najtańszego; pozostali po kursach na
 * relacji regionalnej, kraj → kraj i ostatnim kursie. Zdanie o kolejności
 * przychodzi z API. Bez oceny złożonej (scoring wyłączony).
 * Dokładność dopasowania relacji wybiera trader (07.10.2026) — zawęża albo
 * poszerza listę, przy przewoźniku stoi poziom jego najbliższego kursu.
 * Historia nie dowodzi, że przewoźnik ma dziś wolne auto — ekran mówi to
 * wprost; wybór zostaje u tradera.
 */
function CarrierSuggestionsPanel({
  onPrepareInquiry,
  prepareInquiryBlocked = null,
}: {
  /** I.10 — przygotowanie zapytania do przewoźnika z tego wiersza. */
  onPrepareInquiry?: (carrier: { id: number; name: string }) => void;
  /**
   * Powód, dla którego zapytania nie da się dziś przygotować (z kontekstu
   * API zapytań) — przyciski są wtedy wyszarzone z tym powodem w podpowiedzi
   * zamiast odmowy po kliknięciu. `null` = można.
   */
  prepareInquiryBlocked?: string | null;
}) {
  const record = useRecordContext<RaRecord>();
  const notify = useNotify();
  const stillMounted = useStillMounted();
  const [result, setResult] = useState<CarrierSuggestionResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [precision, setPrecision] = useState<MatchPrecision>(DEFAULT_PRECISION);
  // Dokładność relacji i „Dopasowanie” — za przełącznikiem
  // (decyzja użytkownika 12.10.2026); bez niego karta jak na produkcji
  // 0.31.3: lista kraj → kraj, zapytanie bez `?precision=`.
  const precisionOn = useFeature(FEATURE_CARRIER_RELATION_PRECISION);
  // Szybka zmiana dokładności — wynik pokazuje tylko ostatnie zapytanie.
  const lastRequest = useRef(0);

  if (!record) return null;

  const load = async (wanted: MatchPrecision = precision) => {
    const request = ++lastRequest.current;
    setBusy(true);
    try {
      const { data } = await axiosNest.get<CarrierSuggestionResult>(
        suggestionsUrl(record.id, precisionOn ? wanted : DEFAULT_PRECISION),
      );
      if (request === lastRequest.current) setResult(data);
    } catch (e) {
      // Błąd odczytu dla zlecenia, z którego trader już wyszedł — milczy.
      if (stillMounted() && request === lastRequest.current)
        notify(toErrorMessage(e), { type: 'error' });
    } finally {
      if (request === lastRequest.current) setBusy(false);
    }
  };

  const changePrecision = (wanted: MatchPrecision) => {
    setPrecision(wanted);
    // Lista już na ekranie — od razu w nowej dokładności; przed pierwszym
    // odczytem sam wybór, zapytanie dopiero po „Pokaż przewoźników”.
    if (result !== null) void load(wanted);
  };

  const narrowed =
    precisionOn && result?.precision !== undefined && result.precision !== DEFAULT_PRECISION;

  return (
    <SectionCard title="Przewoźnicy z historią na tej relacji">
      <Stack
        direction={precisionOn ? { xs: 'column', sm: 'row' } : 'row'}
        spacing={1}
        sx={{ mb: 1, alignItems: precisionOn ? { sm: 'center' } : 'center' }}
      >
        {precisionOn && (
          <TextField
            select
            size="small"
            label="Dokładność relacji"
            value={precision}
            onChange={(e) => changePrecision(e.target.value as MatchPrecision)}
            sx={{ minWidth: 300 }}
          >
            {PRECISION_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </TextField>
        )}
        <Button size="small" variant="outlined" disabled={busy} onClick={() => void load()}>
          {result === null ? 'Pokaż przewoźników' : 'Odśwież'}
        </Button>
        {busy && <CircularProgress size={18} />}
      </Stack>

      {result === null && (
        <Typography variant="body2" color="text.secondary">
          Przewoźnicy, którzy jeździli już na tej relacji (według historii zleceń w TMS). Warto
          zapytać ich, zanim wystawisz zlecenie na giełdę.
        </Typography>
      )}

      {result?.refusal && <Alert severity="info">{result.refusal}</Alert>}

      {/* Brzmienie z omówienia 01.10.2026. */}
      {result && !result.refusal && result.suggestions.length === 0 && (
        <Alert severity="info">
          Na relacji {result.relation?.from} → {result.relation?.to} nie było w ostatnich{' '}
          {result.historyDays} dniach żadnego kursu
          {narrowed
            ? ` dopasowanego z tą dokładnością (${result.precisionLabel ?? result.precision}). Wybierz szerszą dokładność.`
            : '.'}
        </Alert>
      )}

      {result && result.suggestions.length > 0 && (
        <>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Relacja {result.relation?.from} → {result.relation?.to}:{' '}
            {plural(result.historyOrders, 'kurs', 'kursy', 'kursów')} w ostatnich{' '}
            {result.historyDays} dniach.
          </Typography>
          {narrowed && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Tylko kursy dopasowane: {result.precisionLabel ?? result.precision}.
            </Typography>
          )}
          {result.ordering && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {result.ordering}
            </Typography>
          )}
          {/* Tabela przewija się w karcie, zamiast rozpychać stronę (audyt układu 23.10.2026): przy
              długiej uwadze („w historii tej relacji brak kursów typu …”) tabela przy 1366 px wychodziła
              poza kartę — strona dostawała przewijanie w bok, a „Przygotuj zapytanie” był ucięty. Od
              23.10.2026 czip uwagi zawija się w obu wersjach; przewijanie zostaje jako zabezpieczenie. */}
          <Box sx={{ overflowX: 'auto' }} data-testid="carrier-suggestions-scroll">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Przewoźnik</TableCell>
                  {/* Nagłówki liczb w jednej linii — przy 1366 px łamały się
                      na dwie (e2e, 28.09.2026). Nagłówek mówi, że to liczba
                      kursów (01.10.2026). */}
                  <TableCell align="right" sx={NOWRAP}>
                    Kursy (region)
                  </TableCell>
                  <TableCell align="right" sx={NOWRAP}>
                    Kursy (kraj → kraj)
                  </TableCell>
                  <TableCell>Ostatni kurs</TableCell>
                  <TableCell align="right" sx={NOWRAP}>
                    <Tooltip title={PRICE_PER_KM_HINT} describeChild>
                      <span>$</span>
                    </Tooltip>
                  </TableCell>
                  {/* Za „$” — kolumny przed ceną zostają jak przed 07.10. */}
                  {precisionOn && <TableCell sx={NOWRAP}>Dopasowanie</TableCell>}
                  <TableCell>Uwagi</TableCell>
                  {onPrepareInquiry && <TableCell />}
                </TableRow>
              </TableHead>
              <TableBody>
                {result.suggestions.map((s) => (
                  <TableRow key={s.carrierId}>
                    <TableCell>
                      <Link to={`/carriers/${s.carrierId}/show`}>{s.name}</Link>
                      {s.vehicleTypes && s.vehicleTypes.length > 0 && (
                        <Typography variant="caption" color="text.secondary" component="div">
                          {s.vehicleTypes.map((v) => `${v.type} ×${v.orders}`).join(', ')}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">{s.regionOrders}</TableCell>
                    <TableCell align="right">{s.countryOrders}</TableCell>
                    <TableCell>{clientDay(s.lastOrderAt)}</TableCell>
                    <TableCell align="right" sx={NOWRAP}>
                      <PricePerKmCell price={s.pricePerKm} />
                    </TableCell>
                    {precisionOn && (
                      <TableCell sx={NOWRAP}>
                        {s.matchLevel ? (
                          <Tooltip title={s.matchLabel ?? ''} describeChild>
                            <span>{MATCH_LEVEL_SHORT[s.matchLevel]}</span>
                          </Tooltip>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                    )}
                    <TableCell>
                      {s.warnings.length === 0
                        ? '—'
                        : s.warnings.map((w) => (
                            <Chip
                              key={w}
                              size="small"
                              color={s.active === false ? 'error' : 'default'}
                              label={w}
                              sx={WRAPPING_CHIP_SX}
                            />
                          ))}
                    </TableCell>
                    {onPrepareInquiry && (
                      <TableCell>
                        {/* Wyszarzony z powodem z API, zanim ktoś kliknie
                            (omówienie 01.10.2026). Wyłączony przycisk nie
                            oddaje zdarzeń myszy — podpowiedź siedzi na owijce. */}
                        <Tooltip title={prepareInquiryBlocked ?? ''} describeChild>
                          <span>
                            <Button
                              size="small"
                              sx={NOWRAP}
                              disabled={prepareInquiryBlocked !== null}
                              onClick={() => onPrepareInquiry({ id: s.carrierId, name: s.name })}
                            >
                              Przygotuj zapytanie
                            </Button>
                          </span>
                        </Tooltip>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
          <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1 }}>
            Historia kursów nie mówi, czy przewoźnik ma dziś wolne auto.
            {result.suggestions.every((s) => s.pricePerKm?.pln == null) &&
              ' Kolumna „$” jest pusta — po najechaniu na kreskę widać, czego zabrakło.'}
          </Typography>
        </>
      )}
    </SectionCard>
  );
}

/** Stan panelu należy do rekordu — przejście na inny rekord zaczyna od zera (`recordScoped`). */
export const CarrierSuggestions = recordScoped(CarrierSuggestionsPanel);
